import { useState, useRef, type FormEvent } from 'react';
import { X, Search, Users, HelpCircle, Check, ArrowRight, FileText, Upload, GitCompare } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Remark, Role, RemarkPilotStatus } from '@/types/api';
import { normSeverity, GIP_STATUSES } from '@/types/api';
import { api } from '@/api/client';
import { Button } from './Button';
import { toast } from '@/state/toast';
import { confirmAction } from '@/state/confirm';
import { formatAgo } from './RemarkCard';
import { ReRemarkModal } from './modals/ReRemarkModal';
import { TransferRemarkModal } from './modals/TransferRemarkModal';
import { DiffViewModal } from './modals/DiffViewModal';
import { MatcherDrawer } from './MatcherDrawer';

interface Props {
  remark: Remark;
  projectId: string;
  onClose: () => void;
  /** Активная роль — определяет видимость GIP-канбан-селектора. */
  role?: Role;
  /** ГИП: переход к загрузке исправленной документации (вкладка «Документы»). */
  onGoToDocs?: () => void;
}

type DetailTab = 'desc' | 'norms' | 'thread' | 'history';

const SEV_COLOR: Record<string, string> = { critical: '#EF4444', high: '#F59E0B', medium: '#6B7280', low: '#9CA3AF' };
const SEV_LABEL: Record<string, string> = { critical: 'Критическое', high: 'Высокое', medium: 'Среднее', low: 'Низкое' };

export function RemarkDetail({ remark, projectId, onClose, role, onGoToDocs }: Props) {
  const isGip = role === 'gip';
  const isCustomer = role === 'customer';
  const [tab, setTab] = useState<DetailTab>('desc');
  const [matcherOpen, setMatcherOpen] = useState(false);
  const [reremarkOpen, setReremarkOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const sev = normSeverity(remark.severity);
  const sevColor = SEV_COLOR[sev];
  const sevLabel = SEV_LABEL[sev];
  const isE = remark.defect_type === 'E-gap';
  const typeLabel = isE ? 'Устранимое' : 'Блокирующее';
  const section = remark.section_code || 'не указан';
  const status = remark.remark_status || (remark.closed_at ? 'closed' : 'open');
  const statusInfo = status === 'closed' ? { label: 'закрыто', color: '#10B981' }
                   : status === 'awaiting_response' ? { label: 'ожидает', color: '#F59E0B' }
                   : { label: 'открыто', color: '#EF4444' };

  const qc = useQueryClient();
  const invalidateRemarkCaches = () => {
    qc.invalidateQueries({ queryKey: ['remarks', projectId] });
    qc.invalidateQueries({ queryKey: ['remarks-dashboard', projectId] });
    qc.invalidateQueries({ queryKey: ['remark', projectId, remark.id] });
  };

  const closeMutation = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/remarks/${remark.id}/close`),
    onSuccess: () => {
      toast.success(`Замечание №${remark.id.slice(0, 8)} закрыто`);
      invalidateRemarkCaches();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось закрыть'),
  });

  // GIP-канбан: select dropdown с 4 статусами (см. red-items). Меняет remarks.status
  // через PATCH /projects/{pid}/remarks/{rid}. Видим только для роли gip.
  // Поле `status` (RemarkPilotStatus: archived/in_progress/awaiting_info/ready_review/blocked)
  // — это пилотный TEXT-маркер, не путать с `remark_status` (open/awaiting_response/closed),
  // который вычисляется на бэке для lifecycle. Это два разных поля по семантике.
  const statusMutation = useMutation({
    mutationFn: (next: RemarkPilotStatus) => api.patch(
      `/projects/${projectId}/remarks/${remark.id}`,
      { status: next },
    ),
    onSuccess: () => {
      toast.success('Статус обновлён');
      invalidateRemarkCaches();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось обновить статус'),
  });

  // DiffView: подгружаем parent_remark если есть. Кнопка появляется в шапке,
  // если у замечания есть parent_remark_id (см. red-items).
  const parentQ = useQuery<{ remark: Remark }>({
    queryKey: ['remark', projectId, remark.parent_remark_id],
    queryFn: () => api.get(`/projects/${projectId}/remarks/${remark.parent_remark_id}`),
    enabled: !!remark.parent_remark_id && diffOpen,
  });

  const handleClose = async () => {
    const ok = await confirmAction({
      title: `Закрыть замечание?`,
      body: 'Действие зафиксируется в истории. ГИП получит уведомление по эл. почте.',
      confirmLabel: 'Закрыть замечание',
    });
    if (ok) closeMutation.mutate();
  };

  return (
    <div
      className="bg-white border border-border rounded-card flex flex-col"
      style={{
        height: 'calc(100vh - 320px)',
        minHeight: 600,
        overflow: 'hidden',
      }}
    >
      <header className="border-b border-border flex items-start justify-between" style={{ padding: '20px 24px' }}>
        <div className="flex items-start" style={{ gap: 12, flex: 1 }}>
          <span style={{ width: 4, height: 24, background: sevColor, borderRadius: 2 }} />
          <div style={{ flex: 1 }}>
            <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: sevColor }}>{sevLabel}</span>
              <Pill bg="#F3F4F6" color="#0A1628">{typeLabel}</Pill>
              <Pill bg="#F3F4F6" color="#0A1628">Раздел: {section}</Pill>
              <Pill bg={status === 'closed' ? 'rgba(16,185,129,.1)' : status === 'awaiting_response' ? 'rgba(245,158,11,.1)' : 'rgba(239,68,68,.1)'} color={statusInfo.color}>{statusInfo.label}</Pill>
            </div>
            <h3
              className="font-heading text-ink mt-2"
              style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >{remark.body.length > 200 ? remark.body.slice(0, 200) + '…' : remark.body}</h3>
          </div>
        </div>
        <div className="flex items-center" style={{ gap: 8 }}>
          {remark.parent_remark_id && (
            <button
              type="button"
              onClick={() => setDiffOpen(true)}
              className="text-muted hover:text-ink rounded-btn flex items-center"
              style={{
                background: '#FFFFFF', border: '1px solid #E5E7EB',
                padding: '6px 10px', gap: 6, fontSize: 12, cursor: 'pointer',
              }}
              title="Сравнить с исходной версией замечания"
            >
              <GitCompare size={14} />
              Сравнить версии
            </button>
          )}
          <button type="button" onClick={onClose} className="text-muted hover:text-ink" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} aria-label="Закрыть детали">
            <X size={20} />
          </button>
        </div>
      </header>

      <nav className="border-b border-border flex" style={{ padding: '0 24px', gap: 4 }}>
        <TabBtn id="desc"    active={tab === 'desc'}    onClick={() => setTab('desc')}>Описание</TabBtn>
        <TabBtn id="norms"   active={tab === 'norms'}   onClick={() => setTab('norms')}>Цитаты норм ({remark.cited_norms?.length || 0})</TabBtn>
        <TabBtn id="thread"  active={tab === 'thread'}  onClick={() => setTab('thread')}>Переписка ({remark.response_count || 0})</TabBtn>
        <TabBtn id="history" active={tab === 'history'} onClick={() => setTab('history')}>История</TabBtn>
      </nav>

      <div className="flex-1 overflow-y-auto" style={{ padding: 24, maxWidth: 720 }}>
        {tab === 'desc'    && <DescriptionTab remark={remark} isCustomer={isCustomer} />}
        {tab === 'norms'   && <NormsTab remark={remark} />}
        {tab === 'thread'  && <ThreadTab remark={remark} projectId={projectId} role={role} />}
        {tab === 'history' && <HistoryTab remark={remark} />}
      </div>

      <footer className="border-t border-border bg-white flex items-center justify-between" style={{ padding: '16px 24px', gap: 12, flexShrink: 0, flexWrap: 'wrap' }}>
        <div className="flex items-center" style={{ gap: 12 }}>
          {!isCustomer && <span className="text-muted tabular" style={{ fontSize: 12 }}>Замечание {remark.id.slice(0, 8)}</span>}
          {role === 'gip' && (
            <GipStatusSelect
              value={(remark.status as RemarkPilotStatus) ?? null}
              onChange={(v) => statusMutation.mutate(v)}
              disabled={statusMutation.isPending}
            />
          )}
        </div>
        {/* Фаза 10.D (2026-07-04): проектировщик НЕ проверяющий — ему НЕ показываем
            «Передать / Повторное замечание / Закрыть». Его действие — загрузить
            исправленную документацию. Рецензентские кнопки — только не-ГИП. */}
        {isGip ? (
          <div className="flex items-center" style={{ gap: 8 }}>
            {onGoToDocs && (
              <Button variant="secondary" size="sm" onClick={() => onGoToDocs()}>Все документы</Button>
            )}
            <Button variant="primary" size="md" icon={<Upload size={16} />} onClick={() => setTab('thread')}>
              Загрузить исправление
            </Button>
          </div>
        ) : (
          <div className="flex items-center" style={{ gap: 8 }}>
            {/* 11.5: кураторские действия заказчику не показываем — у него только приёмка (закрыть). */}
            {!isCustomer && (
              <>
                <Button variant="secondary" size="sm" icon={<Search size={16} />} onClick={() => setMatcherOpen(true)}>Найти похожие</Button>
                <Button variant="secondary" size="sm" icon={<Users size={16} />} onClick={() => setTransferOpen(true)}>Передать</Button>
                <Button variant="secondary" size="sm" icon={<HelpCircle size={16} />} onClick={() => setReremarkOpen(true)} title="Создать повторное замечание — если ответ проектировщика не принят">Повторное замечание</Button>
              </>
            )}
            <Button variant="primary" size="md" icon={<Check size={16} />} onClick={handleClose} disabled={status === 'closed' || closeMutation.isPending}>
              Закрыть замечание
            </Button>
          </div>
        )}
      </footer>

      <MatcherDrawer open={matcherOpen} projectId={projectId} remark={remark} onClose={() => setMatcherOpen(false)} />
      {reremarkOpen && (
        <ReRemarkModal projectId={projectId} remarkId={remark.id} onClose={() => setReremarkOpen(false)} />
      )}
      {transferOpen && (
        <TransferRemarkModal projectId={projectId} remark={remark} onClose={() => setTransferOpen(false)} />
      )}
      {diffOpen && remark.parent_remark_id && parentQ.data?.remark && (
        <DiffViewModal
          before={parentQ.data.remark}
          after={remark}
          onClose={() => setDiffOpen(false)}
        />
      )}
      {diffOpen && remark.parent_remark_id && !parentQ.data?.remark && (
        <DiffLoadingModal isError={parentQ.isError} onClose={() => setDiffOpen(false)} />
      )}
    </div>
  );
}

/** GIP-канбан: select dropdown с 4 статусами (NULL = «не начато»). Заменяет DnD. */
function GipStatusSelect({
  value, onChange, disabled,
}: { value: RemarkPilotStatus; onChange: (v: RemarkPilotStatus) => void; disabled?: boolean }) {
  return (
    <label className="flex items-center" style={{ gap: 6 }}>
      <span className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        Статус
      </span>
      <select
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value as Exclude<RemarkPilotStatus, 'archived'>;
          onChange((v as RemarkPilotStatus) || null);
        }}
        style={{
          padding: '6px 10px', fontSize: 13, borderRadius: 6,
          border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#0A1628',
          cursor: disabled ? 'wait' : 'pointer', fontFamily: 'inherit',
        }}
      >
        <option value="">— не начато —</option>
        {GIP_STATUSES.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </select>
    </label>
  );
}

function DiffLoadingModal({ isError, onClose }: { isError: boolean; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,.6)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-card"
        style={{ padding: 24, border: '1px solid #E5E7EB', maxWidth: 360 }}
      >
        <div className="text-ink" style={{ fontSize: 14 }}>
          {isError ? 'Не удалось загрузить исходную версию.' : 'Загружаем исходную версию замечания…'}
        </div>
        <div className="mt-3 flex justify-end">
          <button type="button" onClick={onClose} className="text-muted" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { id: string; active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '14px 0', marginRight: 16,
        fontWeight: active ? 600 : 500, fontSize: 14,
        color: active ? '#0A1628' : '#6B7280',
        borderBottom: active ? '2px solid #F59E0B' : '2px solid transparent',
        marginBottom: -1, background: 'transparent', border: 'none', cursor: 'pointer',
      }}
    >{children}</button>
  );
}

function Pill({ bg, color, children }: { bg: string; color: string; children: React.ReactNode }) {
  return (
    <span className="rounded-pill" style={{ padding: '2px 8px', fontSize: 11, fontWeight: 500, background: bg, color }}>{children}</span>
  );
}

function DescriptionTab({ remark, isCustomer }: { remark: Remark; isCustomer?: boolean }) {
  return (
    <div>
      <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Полный текст замечания</div>
      <p className="text-ink mt-2" style={{ fontSize: 15, lineHeight: 1.65 }}>{remark.body}</p>

      {/* 11.5: заказчику — только осмысленные поля; snapshot-хеш и пустые
          эксперт/роль/аспект (кураторская кухня) скрыты. */}
      <div className="border-t border-border mt-6 pt-6 grid grid-cols-2" style={{ gap: 16 }}>
        <MetaRow label="Создано" value={remark.created_at ? new Date(remark.created_at).toLocaleString('ru-RU') : '—'} />
        <MetaRow label="Тип" value={remark.defect_type === 'E-gap' ? 'Устранимое' : 'Блокирующее'} />
        {!isCustomer && <MetaRow label="Эксперт" value={remark.expert_name || '—'} />}
        {!isCustomer && <MetaRow label="Редакция" value={remark.snapshot_id.slice(0, 8)} mono />}
        {!isCustomer && <MetaRow label="Назначенная роль" value={remark.specialist_role || '—'} />}
        {!isCustomer && <MetaRow label="Аспект" value={remark.aspect || '—'} />}
      </div>

      {remark.affects_sections && remark.affects_sections.length > 0 && (
        <div className="mt-6">
          <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Затронутые разделы</div>
          <div className="flex" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {remark.affects_sections.map((s, i) => (
              <span key={i} className="rounded-pill bg-surface" style={{ padding: '4px 10px', fontSize: 12, color: '#0A1628' }}>{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
      <div className="text-ink mt-1" style={{ fontSize: 14, fontFamily: mono ? 'monospace' : undefined }}>{value}</div>
    </div>
  );
}

function NormsTab({ remark }: { remark: Remark }) {
  const norms = remark.cited_norms || [];
  if (norms.length === 0) {
    return (
      <p className="text-muted" style={{ fontSize: 14, textAlign: 'center', padding: '32px 0' }}>
        К этому замечанию не привязано норм. Используйте семантический поиск (RAG) в Запросе для проверки.
      </p>
    );
  }
  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {norms.map((norm, i) => (
        <div
          key={i}
          className="rounded-r-card bg-surface"
          style={{ borderLeft: '4px solid #F59E0B', padding: 16 }}
        >
          <div className="font-heading" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#B45309' }}>
            {norm}
          </div>
          <p className="text-ink mt-1.5 italic" style={{ fontSize: 14, lineHeight: 1.55 }}>
            Цитата нормы — для просмотра полного текста используйте Запрос → Семантический поиск
          </p>
        </div>
      ))}
    </div>
  );
}

interface LinkedVersion { version_id: string; document_title: string; version_seq: number; created_at: string }
interface DocItem { document_id: string; version_id: string; title: string; version_seq: number }

/** 10.B3: исправленные версии тома, связанные с замечанием. Все видят список;
 *  проектировщик может приложить новую версию (загрузка + связь с замечанием). */
function CorrectionVersions({ projectId, remarkId, isGip }: { projectId: string; remarkId: string; isGip: boolean }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [docId, setDocId] = useState('');

  const linkedQ = useQuery<{ versions: LinkedVersion[] }>({
    queryKey: ['remark-linked-versions', projectId, remarkId],
    queryFn: () => api.get(`/projects/${projectId}/remarks/${remarkId}/linked-versions`),
  });
  const docsQ = useQuery<{ documents: DocItem[] }>({
    queryKey: ['documents', projectId],
    queryFn: () => api.get(`/projects/${projectId}/documents`),
    enabled: isGip,
  });
  // Уникальные тома (по document_id) для выбора, какой том исправляем.
  const docs = Array.from(
    new Map((docsQ.data?.documents || []).map((d) => [d.document_id, d])).values()
  );

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('project_id', projectId);
      fd.append('file', file);
      if (docId) fd.append('document_id', docId);
      fd.append('remark_id', remarkId);
      const res = await fetch('/ingest', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b?.detail) detail = String(b.detail); } catch { /* ignore */ }
        throw new Error(detail);
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success('Исправленная версия загружена и связана с замечанием');
      qc.invalidateQueries({ queryKey: ['remark-linked-versions', projectId, remarkId] });
      qc.invalidateQueries({ queryKey: ['documents', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось загрузить'),
  });

  const linked = linkedQ.data?.versions || [];
  if (linked.length === 0 && !isGip) return null;  // заказчику пустой блок не показываем

  return (
    <div className="rounded-card border border-border" style={{ padding: '12px 14px', background: '#FAFAFA' }}>
      <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 600, marginBottom: linked.length ? 8 : 0 }}>
        Исправленная документация по замечанию
      </div>
      {linked.map((v) => (
        <div key={v.version_id} className="flex items-center" style={{ gap: 8, fontSize: 12.5, padding: '4px 0' }}>
          <FileText size={14} stroke="#10B981" />
          <span className="text-ink">{v.document_title}</span>
          <span className="text-muted tabular">версия файла {v.version_seq}</span>
          <span className="text-muted ml-auto tabular" style={{ fontSize: 11 }}>{new Date(v.created_at).toLocaleDateString('ru-RU')}</span>
        </div>
      ))}
      {linked.length === 0 && isGip && (
        <div className="text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
          Загрузите исправленную версию тома — она станет новой версией файла и свяжется с этим замечанием.
        </div>
      )}
      {isGip && (
        <div className="flex items-center" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <select value={docId} onChange={(e) => setDocId(e.target.value)}
            style={{ flex: 1, minWidth: 180, padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12.5 }}>
            <option value="">Новый документ (не версия существующего тома)</option>
            {docs.map((d) => <option key={d.document_id} value={d.document_id}>{d.title}</option>)}
          </select>
          <input ref={fileRef} type="file" accept=".pdf,.dxf,.xlsx,.xls,.pptx,.html,.htm" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
            style={{ padding: '7px 14px', borderRadius: 16, fontSize: 12.5, fontWeight: 600, background: '#0A1628', color: '#FFF', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {upload.isPending ? 'Загрузка…' : 'Загрузить версию'}
          </button>
        </div>
      )}
    </div>
  );
}

function ThreadTab({ remark, projectId, role }: { remark: Remark; projectId: string; role?: Role }) {
  const responses = remark.responses || [];
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  // Заказчик/куратор проверяет ответ проектировщика: принять (закрыть замечание)
  // или отклонить (вернуть в работу). Проектировщику эти кнопки не показываем.
  const isReviewer = role !== 'gip';
  const isClosed = (remark.remark_status || (remark.closed_at ? 'closed' : 'open')) === 'closed';
  const review = useMutation({
    mutationFn: ({ responseId, action }: { responseId: string; action: 'accept' | 'reject' }) =>
      api.post(`/projects/${projectId}/remarks/${remark.id}/responses/${responseId}/${action}`, {}),
    onSuccess: (_r, v) => {
      toast.success(v.action === 'accept' ? 'Ответ принят — замечание закрыто' : 'Ответ отклонён — замечание возвращено в работу');
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      qc.invalidateQueries({ queryKey: ['remarks-dashboard', projectId] });
      qc.invalidateQueries({ queryKey: ['remark', projectId, remark.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось выполнить'),
  });

  // Фаза 10.B1 (2026-07-04): раньше слался JSON {body} и файлы НЕ уходили. Теперь
  // multipart — текст + реальные вложения (бэкенд create_response их принимает).
  const send = useMutation({
    mutationFn: async (body: string) => {
      const fd = new FormData();
      fd.append('body', body);
      for (const f of files) fd.append('files', f);
      const res = await fetch(`/projects/${projectId}/remarks/${remark.id}/responses`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b?.detail) detail = String(b.detail); } catch { /* ignore */ }
        throw new Error(detail);
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success(files.length > 0 ? 'Ответ и вложения отправлены' : 'Ответ отправлен');
      setText(''); setFiles([]); setInternal(false);
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      qc.invalidateQueries({ queryKey: ['remarks-dashboard', projectId] });
      qc.invalidateQueries({ queryKey: ['remark', projectId, remark.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось отправить'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    send.mutate(text.trim());
  };

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {/* 10.B3: исправленные версии тома, приложенные к замечанию — видят все;
          проектировщик может приложить новую (загрузка версии + связь). */}
      <CorrectionVersions projectId={projectId} remarkId={remark.id} isGip={role === 'gip'} />

      {responses.length === 0 && (
        <p className="text-muted" style={{ fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
          Пока ни одного ответа. Первый ответит — ГИП.
        </p>
      )}
      {responses.map((r) => (
        <div key={r.id} className="flex" style={{ gap: 12 }}>
          <div className="rounded-pill bg-surface border border-border flex items-center justify-center shrink-0" style={{ width: 36, height: 36, fontSize: 13, fontWeight: 600 }}>ГП</div>
          <div className="bg-white border border-border rounded-card flex-1" style={{ padding: '12px 16px' }}>
            <div className="flex items-center justify-between">
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>ГИП</span>
              <span className="text-muted tabular" style={{ fontSize: 12 }}>{formatAgo(r.created_at)}</span>
            </div>
            <p className="text-ink mt-1.5" style={{ fontSize: 14, lineHeight: 1.55 }}>{r.body}</p>
            {isReviewer && !isClosed && (
              <div className="flex items-center" style={{ gap: 8, marginTop: 10 }}>
                <button type="button" onClick={() => review.mutate({ responseId: r.id, action: 'accept' })}
                  disabled={review.isPending}
                  style={{ padding: '5px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600, background: '#10B981', color: '#FFF', border: 'none', cursor: 'pointer' }}>
                  Принять (закрыть)
                </button>
                <button type="button" onClick={() => review.mutate({ responseId: r.id, action: 'reject' })}
                  disabled={review.isPending}
                  style={{ padding: '5px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600, background: 'transparent', color: '#B91C1C', border: '1px solid #FCA5A5', cursor: 'pointer' }}>
                  Отклонить (вернуть)
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      <form onSubmit={submit} className="mt-4">
        <label className="inline-flex items-center text-muted" style={{ fontSize: 12, gap: 8, marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} style={{ accentColor: '#F59E0B', width: 14, height: 14 }} />
          Внутренний комментарий (виден только экспертам)
        </label>
        {files.length > 0 && (
          <div className="mb-2 flex flex-col" style={{ gap: 6 }}>
            {files.map((f, i) => (
              <div key={i} className="flex items-center bg-surface rounded-btn" style={{ padding: '6px 10px', gap: 8, fontSize: 12 }}>
                <FileText size={14} stroke="#6B7280" />
                <span style={{ flex: 1 }}>{f.name}</span>
                <span className="text-muted tabular">{(f.size / 1024).toFixed(0)} КБ</span>
                <button type="button" onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))} className="text-muted" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center" style={{ gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.dxf,.dwg"
            onChange={(e) => { if (e.target.files) setFiles([...files, ...Array.from(e.target.files)]); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="rounded-btn flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, background: '#FFFFFF', border: '1px solid #E5E7EB', cursor: 'pointer' }}
            aria-label="Прикрепить"
            title="Прикрепить файлы"
          >
            <Upload size={18} stroke="#6B7280" />
          </button>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(e as unknown as FormEvent); }}
            placeholder="Текст ответа… (Ctrl+Enter — отправить)"
            className="flex-1 bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '12px 14px', fontSize: 14 }}
          />
          <button
            type="submit"
            disabled={!text.trim() || send.isPending}
            className="rounded-btn flex items-center justify-center shrink-0 disabled:opacity-50"
            style={{ width: 44, height: 44, background: '#F59E0B', color: '#0A1628', border: 'none', cursor: send.isPending ? 'wait' : 'pointer' }}
            aria-label="Отправить"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}

function HistoryTab({ remark }: { remark: Remark }) {
  const events: { time: string; title: string; sub: string }[] = [];
  if (remark.created_at) events.push({ time: remark.created_at, title: 'Замечание создано', sub: `${remark.expert_name || 'Эксперт'} · ${formatAgo(remark.created_at)}` });
  if (remark.responses) {
    remark.responses.forEach((r, i) => {
      events.push({ time: r.created_at || '', title: `Ответ ГИП №${i + 1}`, sub: formatAgo(r.created_at) });
    });
  }
  if (remark.closed_at) events.push({ time: remark.closed_at, title: 'Замечание закрыто', sub: formatAgo(remark.closed_at) });

  if (events.length === 0) {
    return <p className="text-muted" style={{ fontSize: 14, textAlign: 'center', padding: '32px 0' }}>История пуста.</p>;
  }

  return (
    <ol className="flex flex-col" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 0 }}>
      {events.map((ev, i) => (
        <li key={i} className="flex" style={{ gap: 12, paddingBottom: 20 }}>
          <div style={{ width: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B', marginTop: 4 }} />
            {i < events.length - 1 && <span style={{ flex: 1, width: 1, background: '#E5E7EB', marginTop: 4 }} />}
          </div>
          <div style={{ flex: 1 }}>
            <div className="text-ink" style={{ fontSize: 14, fontWeight: 500 }}>{ev.title}</div>
            <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{ev.sub}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}
